import React from 'react';
import PropTypes from 'prop-types';
import View from 'components/view';
import { Form, Field, Fieldgroup } from 'components/form';
import SectionHeader from 'components/section-header';
import AppSettings from 'components/apps/settings';
import ThemeSupportAlert from './theme-support-alert';
import StorefrontHeadlessDocs from './headless-docs';
import { getSettingContentModels } from 'utils/content/app';

export default class StorefrontSettings extends React.PureComponent {
  static contextTypes = {
    client: PropTypes.object.isRequired,
  };

  render() {
    const {
      record: storefront,
      values,
      settings,
      edited,
      storefrontApp,
      appConfigs,
      onSubmitSettings,
      onChangeSettings,
      viewProps = {},
    } = this.props;

    const { client } = this.context;

    const urlPlaceholder = `https://www.${client.id || 'example'}.com`;
    const isExternalV1 = Boolean(storefront.external?.custom);
    const isExternalApp = storefrontApp?.app.storefront?.external;
    const settingContentModels =
      storefrontApp && getSettingContentModels(storefrontApp, appConfigs);

    return (
      <View
        {...viewProps}
        headerActions={[
          ...viewProps.headerActions,
          {
            label: 'Save changes',
            type: edited ? 'default' : 'secondary disabled',
            submit: true,
          },
        ]}
        form={this.refs.form}
        localized={true}
      >
        <Form
          ref="form"
          onSubmit={onSubmitSettings}
          onChange={onChangeSettings}
          values={values}
        >
          {storefront.hosted && <ThemeSupportAlert />}
          <div className="view-body-section-group">
            {isExternalV1 ? (
              <fieldset>
                <div className="view-body-section">
                  <div className="view-section-content no-header">
                    <div className="row">
                      <Field
                        type="text"
                        name="_storefront.external.custom_url"
                        label="Storefront URL"
                        defaultValue={values._storefront.external?.custom_url}
                        placeholder={urlPlaceholder}
                        rules="secure_url,external_url"
                        className="span4"
                        help="URL should point to a storefront application"
                      />
                    </div>
                  </div>
                </div>
                <br />
                <Fieldgroup
                  label="Framework & hosting docs"
                  defaultActive={false}
                >
                  <StorefrontHeadlessDocs />
                </Fieldgroup>
              </fieldset>
            ) : (
              <>
                {settingContentModels && (
                  <AppSettings
                    {...this.props}
                    record={settings}
                    values={values}
                    settingContentModels={settingContentModels}
                  />
                )}

                {isExternalApp && (
                  <div className="view-body-section">
                    <div className="view-body-subheader">
                      <SectionHeader
                        className="view-body-subtitle"
                        title="Use Swell domain"
                        subtitle="Customers will access the store using your Swell domain while requests are proxied to an external storefront"
                      />
                      <div className="view-body-actions">
                        <Field
                          type="toggle"
                          name="proxy"
                          label="Enabled"
                          defaultChecked={values.proxy ?? true}
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="view-body-section">
                  <a name="password" ref="passwordAnchor" />
                  <div className="view-body-subheader">
                    <SectionHeader
                      className="view-body-subtitle"
                      title="Password protection"
                      subtitle="Enable a password to restrict access to your storefront"
                    />
                    <div className="view-body-actions">
                      <Field
                        type="toggle"
                        name="_storefront.hosting.password_enabled"
                        label="Enabled"
                        defaultChecked={storefront.hosting?.password_enabled}
                      />
                    </div>
                  </div>
                  {values._storefront.hosting?.password_enabled && (
                    <div className="view-section-content">
                      <div className="row">
                        <Field
                          type="text"
                          name="_storefront.hosting.password"
                          label="Password"
                          defaultValue={storefront.hosting?.password}
                          hint="Visitors will be asked to enter this password if enabled"
                          rules="password"
                          required={true}
                        />
                      </div>
                      <div className="row"></div>
                      <Field
                        type="textarea"
                        name="_storefront.hosting.password_message"
                        label="Message to visitors"
                        defaultValue={storefront.hosting?.password_message}
                        rows={3}
                        maxRows={5}
                        autoSize={true}
                        hint="This will be displayed to visitors when they're asked to log in"
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </Form>
      </View>
    );
  }
}
